/* eslint-disable react/no-array-index-key */
import * as React from 'react'
import styled from 'styled-components'
import { compose, layout, LayoutProps } from 'styled-system'
import { FormikHelpers } from 'formik'

import { NewPasswordProps, PasswordPayload } from '@ally/transmitigator'
import { BadWords } from '@ally/use-validation'
import { Box, Button, Space, TextBody } from '@ally/metronome-ui'
import { Form, Forms } from '@ally/forms'
import { LoginHeading } from '@ally/ally-login'

import { useTransmit } from '../TransmitContext'
import { CreatePassword, PasswordHint } from './Enrollment'
import { CIAMErrorBanner } from './Shared'
import { useFlagContextValue } from '../FlagContext'
import { FeatureFlag } from '../Utils'

const StyledForm = styled(Form)<LayoutProps>`
  ${compose(layout)};
  width: 100%;
`
const FORM_ID = 'ally-login-new-password-form'

interface NewPasswordForm {
  password: string
  passwordHint: string
}

const initialValues: NewPasswordForm = {
  password: '',
  passwordHint: '',
}

export const NewPasswordContent: React.FC<NewPasswordProps> = ({
  onComplete,
  status,
  errorCode,
  username,
  isBiometricEnrolled,
}) => {
  const { hostServices } = useTransmit()
  const { variation } = useFlagContextValue()

  const isBiometricsPWUnEnrollmentEnabled: boolean = variation(
    FeatureFlag.BiometricsPWUnEnrollment,
    false,
  )

  const [errorState, setErrorState] = React.useState('')

  const badWords = hostServices?.hostData.badWords.data
  const badWordsObject: BadWords = badWords || {
    base: [],
    names: [],
    substring: [],
  }

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Forgot Password:Create New Password',
    })
  }, [])

  React.useEffect(() => {
    if (status !== 'Loading') setErrorState(errorCode || '')
  }, [errorCode, status])

  const handleSubmit = (
    formOutput: NewPasswordForm,
    setSubmitting: (isSubmitting: boolean) => void,
  ): void => {
    const payload: PasswordPayload = {
      newPassword: formOutput.password,
      confirmPassword: formOutput.password,
      passwordHint: formOutput.passwordHint,
    }
    onComplete(payload)

    setSubmitting(false)
  }

  const onSubmit = (
    formOutput: NewPasswordForm,
    { setSubmitting }: FormikHelpers<NewPasswordForm>,
  ): void => {
    if (status !== 'Loading') {
      setErrorState('')
      handleSubmit(formOutput, setSubmitting)
    }
  }
  return (
    <>
      <LoginHeading title="Create a new password" />

      {isBiometricsPWUnEnrollmentEnabled && isBiometricEnrolled && (
        <Space mb="sm">
          <TextBody tag="p" size="sm">
            If you use biometric login (like Face ID&reg; or fingerprint) for
            our app, you&apos;ll have to re-enroll after changing your password.
          </TextBody>
        </Space>
      )}

      <CIAMErrorBanner error={errorState} />

      <Box display="flex">
        <Forms
          formId={FORM_ID}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          <StyledForm id={FORM_ID} autoComplete="off">
            <CreatePassword
              username={username}
              title="New password"
              fieldName="password"
            />
            <PasswordHint badWords={badWordsObject} title="Password hint" />
            <Button
              withAllyTM="forgotPassword-savePassword"
              type="submit"
              variant="primary"
              text="Save Password"
              loading={status === 'Loading'}
            />
          </StyledForm>
        </Forms>
      </Box>
    </>
  )
}

export const NewPassword: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'NewPassword') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <NewPasswordContent {...activeScreen.payload} />
}
